import { cn } from "@/lib/utils";

function SkeletonPulse({ className }: { className?: string }) {
  return (
    <div className={cn("bg-muted animate-pulse", className)} />
  );
}

export function ProductSkeleton({ className }: { className?: string }) {
  return (
    <div className={className}>
      <SkeletonPulse className="aspect-[4/5] w-full rounded-none" />
      <div className="mt-3 flex items-start justify-between gap-2">
        <div className="flex-1">
          <SkeletonPulse className="h-3 w-16 rounded-sm" />
          <SkeletonPulse className="mt-1.5 h-5 w-3/4 rounded-sm" />
        </div>
        <SkeletonPulse className="mt-0.5 h-5 w-10 rounded-sm" />
      </div>
    </div>
  );
}

export function CreatorSkeleton({ className }: { className?: string }) {
  return (
    <div className={className}>
      <SkeletonPulse className="aspect-[4/5] w-full rounded-none" />
      <div className="mt-4 flex items-center gap-3">
        <SkeletonPulse className="h-10 w-10 rounded-full" />
        <div className="flex-1">
          <SkeletonPulse className="h-5 w-32 rounded-sm" />
          <SkeletonPulse className="mt-1 h-3 w-24 rounded-sm" />
        </div>
      </div>
    </div>
  );
}

export function CollectionSkeleton({ className }: { className?: string }) {
  return (
    <div className={className}>
      <SkeletonPulse className="aspect-[4/5] w-full rounded-none" />
      <div className="mt-5 flex items-center justify-between">
        <div className="flex-1">
          <SkeletonPulse className="h-5 w-40 rounded-sm" />
          <SkeletonPulse className="mt-1.5 h-3 w-24 rounded-sm" />
        </div>
        <div className="flex gap-2">
          <SkeletonPulse className="h-4 w-4 rounded-sm" />
          <SkeletonPulse className="h-4 w-4 rounded-sm" />
        </div>
      </div>
    </div>
  );
}

export function StatSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("border border-border bg-card p-6", className)}>
      <SkeletonPulse className="h-3 w-24 rounded-sm" />
      <SkeletonPulse className="mt-3 h-8 w-20 rounded-sm" />
      <SkeletonPulse className="mt-1 h-3 w-32 rounded-sm" />
    </div>
  );
}

export function ListItemSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-3 px-4 py-3", className)}>
      <SkeletonPulse className="h-10 w-10 rounded object-cover" />
      <div className="min-w-0 flex-1">
        <SkeletonPulse className="h-3 w-3/4 rounded-sm" />
        <SkeletonPulse className="mt-1 h-3 w-1/2 rounded-sm" />
      </div>
      <SkeletonPulse className="h-3 w-10 rounded-sm" />
    </div>
  );
}

export function ProfileSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("max-w-3xl space-y-8", className)}>
      <div>
        <SkeletonPulse className="h-8 w-48 rounded-sm" />
        <SkeletonPulse className="mt-2 h-4 w-64 rounded-sm" />
      </div>
      <div className="grid gap-6 md:grid-cols-[140px_1fr]">
        <SkeletonPulse className="h-32 w-32 rounded-full" />
        <div className="space-y-3">
          <SkeletonPulse className="h-10 w-full rounded-sm" />
          <SkeletonPulse className="h-24 w-full rounded-sm" />
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <SkeletonPulse key={i} className="h-10 w-full rounded-sm" />
        ))}
      </div>
    </div>
  );
}

export function TableRowSkeleton({ cols = 6, className }: { cols?: number; className?: string }) {
  return (
    <tr className={className}>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-2">
          <SkeletonPulse className="h-3 w-full rounded-sm" />
        </td>
      ))}
    </tr>
  );
}

/** Full-page skeleton for /creator/$handle */
export function CreatorPageSkeleton() {
  return (
    <div>
      <SkeletonPulse className="h-[60vh] w-full rounded-none" />
      <section className="mx-auto -mt-24 max-w-5xl px-6 text-center">
        <SkeletonPulse className="mx-auto h-32 w-32 rounded-full border-4 border-background" />
        <SkeletonPulse className="mx-auto mt-6 h-10 w-64 rounded-sm" />
        <SkeletonPulse className="mx-auto mt-3 h-3 w-40 rounded-sm" />
        <SkeletonPulse className="mx-auto mt-6 h-4 w-80 rounded-sm" />
      </section>
      <section className="mx-auto max-w-7xl px-6 pt-24 pb-24">
        <SkeletonPulse className="h-3 w-24 rounded-sm" />
        <SkeletonPulse className="mt-3 h-8 w-72 rounded-sm" />
        <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-14 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <ProductSkeleton key={i} />
          ))}
        </div>
      </section>
    </div>
  );
}

/** Full-page skeleton for /product/$id */
export function ProductPageSkeleton() {
  return (
    <div>
      <section className="grid grid-cols-1 md:grid-cols-2">
        <SkeletonPulse className="aspect-[4/5] w-full rounded-none md:aspect-auto md:h-[80vh]" />
        <div className="flex flex-col justify-center px-6 py-12 md:px-16">
          <SkeletonPulse className="h-3 w-20 rounded-sm" />
          <SkeletonPulse className="mt-3 h-10 w-3/4 rounded-sm" />
          <SkeletonPulse className="mt-4 h-8 w-24 rounded-sm" />
          <div className="mt-10 flex gap-3">
            <SkeletonPulse className="h-14 flex-1 rounded-none" />
            <SkeletonPulse className="h-14 w-14 rounded-none" />
          </div>
          <SkeletonPulse className="mt-6 h-3 w-64 rounded-sm" />
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-6 pt-24 pb-24">
        <SkeletonPulse className="h-3 w-24 rounded-sm" />
        <SkeletonPulse className="mt-3 h-8 w-56 rounded-sm" />
        <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-14 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <ProductSkeleton key={i} />
          ))}
        </div>
      </section>
    </div>
  );
}

/** Skeleton for collection editor — cover + meta + grids */
export function CollectionEditorSkeleton() {
  return (
    <div className="space-y-10">
      <SkeletonPulse className="h-4 w-32 rounded-sm" />
      <div className="grid gap-6 md:grid-cols-[260px_1fr]">
        <SkeletonPulse className="aspect-[4/5] w-full rounded-none" />
        <div className="space-y-4">
          <SkeletonPulse className="h-10 w-full rounded-sm" />
          <SkeletonPulse className="h-10 w-full rounded-sm" />
        </div>
      </div>
      <div>
        <SkeletonPulse className="h-6 w-56 rounded-sm" />
        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <ProductSkeleton key={i} />
          ))}
        </div>
      </div>
      <div>
        <SkeletonPulse className="h-6 w-64 rounded-sm" />
        <SkeletonPulse className="mt-1 h-3 w-80 rounded-sm" />
        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <ProductSkeleton key={i} />
          ))}
        </div>
      </div>
    </div>
  );
}
